class EbTheme {
  constructor() {
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.applyMotionPreference();
    this.motionQuery.addEventListener('change', () => this.applyMotionPreference());
    document.addEventListener('submit', (event) => this.handleProductFormFallback(event));
  }

  applyMotionPreference() {
    document.body.classList.toggle('eb-reduced-motion', this.motionQuery.matches);
  }

  handleProductFormFallback(event) {
    if (event.defaultPrevented || !window.fetch) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.isCartAddForm(form)) return;

    event.preventDefault();
    const formData = new FormData(form);
    const id = Number(formData.get('id') || 0);
    const quantity = Number(formData.get('quantity') || 1);
    const submitter = event.submitter instanceof HTMLElement ? event.submitter : form.querySelector('[type="submit"]');
    const originalLabel = submitter?.textContent;

    if (submitter) submitter.setAttribute('aria-busy', 'true');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then((response) => {
        if (!response.ok) throw response;
        return response.json();
      })
      .then((item) => {
        const lineId = id || Number(item.id || item.variant_id || 0);
        const lineQuantity = quantity > 0 ? quantity : 1;
        document.dispatchEvent(new CustomEvent('eb:cart:add', {
          detail: {
            item,
            lines: lineId ? [{ id: lineId, quantity: lineQuantity }] : [],
          },
        }));
      })
      .catch(() => {
        form.querySelector('[role="status"], [aria-live="polite"]')?.replaceChildren(document.createTextNode(''));
      })
      .finally(() => {
        if (submitter) {
          submitter.removeAttribute('aria-busy');
          if (originalLabel != null && submitter.textContent !== originalLabel) submitter.textContent = originalLabel;
        }
      });
  }

  isCartAddForm(form) {
    if (!form.querySelector('[name="id"]')) return false;
    try {
      return new URL(form.action, window.location.origin).pathname.endsWith('/cart/add');
    } catch (error) {
      return String(form.getAttribute('action') || '').includes('/cart/add');
    }
  }
}

class EbLocalization extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ebLocalizationInit === '1') return;
    this.dataset.ebLocalizationInit = '1';

    this.trigger = this.querySelector('[data-eb-loc-trigger]');
    this.panel = this.querySelector('[data-eb-loc-panel]');
    if (!this.trigger || !this.panel) return;

    this.open = false;
    this.search = this.querySelector('[data-eb-loc-search]');
    this.rows = this.querySelector('[data-eb-loc-rows]');

    this.trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggle();
    });

    if (this.search && this.rows) {
      this.items = [...this.rows.querySelectorAll('.eb-loc__row')];
      this.empty = document.createElement('p');
      this.empty.className = 'eb-loc__empty';
      this.empty.hidden = true;
      this.empty.textContent = this.search.dataset.emptyText || 'No matches';
      this.rows.after(this.empty);
      this.search.addEventListener('input', () => this.filter());
    }

    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.open) {
        this.close();
        this.trigger.focus();
      }
    };
  }

  toggle() {
    if (this.open) this.close();
    else this.show();
  }

  show() {
    this.open = true;
    this.panel.hidden = false;
    this.setAttribute('data-open', '');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    if (this.search) requestAnimationFrame(() => this.search.focus());
  }

  close() {
    if (!this.open) return;

    this.open = false;
    this.panel.hidden = true;
    this.removeAttribute('data-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  filter() {
    const query = this.search.value.trim().toLowerCase();
    let visibleCount = 0;

    this.items.forEach((item) => {
      const matches = !query || (item.dataset.ebLocName || item.textContent).toLowerCase().includes(query);
      item.hidden = !matches;
      if (matches) visibleCount += 1;
    });

    if (this.empty) this.empty.hidden = visibleCount > 0;
  }
}

if (!customElements.get('eb-localization')) {
  customElements.define('eb-localization', EbLocalization);
}

document.addEventListener('DOMContentLoaded', () => {
  window.ebTheme = new EbTheme();
});
